import React, { useEffect, useState } from 'react';
import { Plus, ClipboardList, Eye } from 'lucide-react';
import { Card, Btn, Input, Field, Modal, Select, Table, Spinner, ErrBox } from '../../../components/UI';
import { inventoryAPI } from '../../../utils/api';
import { fmtDate } from '../../../utils/format';

export default function OpnameTab() {
  const [opnames, setOpnames] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewOpname, setViewOpname] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ opname_date: new Date().toISOString().split('T')[0], warehouse_id: '' });

  useEffect(() => { loadOpnames(); loadLocations(); }, []);

  const loadLocations = async () => {
    try {
      const r = await inventoryAPI.getLocations();
      setLocations((r.data || []).map(x => ({ value: x.id, label: x.nama })));
    } catch {}
  };

  const loadOpnames = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await inventoryAPI.getOpnames();
      setOpnames(r.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.opname_date || !form.warehouse_id) return alert('Tanggal dan lokasi wajib diisi');
    setSaving(true);
    try {
      await inventoryAPI.createOpname(form);
      closeModal();
      loadOpnames();
    } catch (e) { alert('Gagal menyimpan: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleReconcile = async (id) => {
    if (!window.confirm('Reconcile session opname ini? Stok akan disesuaikan dengan jumlah fisik.')) return;
    try {
      const opname = opnames.find(o => o.id === id);
      if (!opname) return;
      await inventoryAPI.reconcileOpname(id, { warehouse_id: opname.warehouse_id });
      alert('Reconciliation selesai');
      loadOpnames();
    } catch (e) { alert('Gagal: ' + e.message); }
  };

  const handleView = async (id) => {
    try {
      const r = await inventoryAPI.getOpname(id);
      setViewOpname(r.data);
    } catch (e) { alert(e.message); }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ opname_date: new Date().toISOString().split('T')[0], warehouse_id: '' });
  };

  const getStatusBadge = (status) => {
    const cfg = { DRAFT: { bg: '#F3F4F6', color: '#374151' }, IN_PROGRESS: { bg: '#DBEAFE', color: '#1E40AF' }, COMPLETED: { bg: '#ECFDF5', color: '#059669' }, DISCREPANCY: { bg: '#FEF2F2', color: '#DC2626' } };
    const c = cfg[status] || cfg.DRAFT;
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>;
  if (error) return <div style={{ marginBottom: 16 }}><ErrBox msg={error} onRetry={loadOpnames} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn icon={<Plus size={14}/>} onClick={() => setShowModal(true)}>Session Opname Baru</Btn>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <ClipboardList size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Stock Opname ({opnames.length})
          </h3>
        </div>
        {opnames.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            <ClipboardList size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Belum ada session opname</p>
          </div>
        ) : (
          <Table pageSize={10} cols={[
            { key: 'opname_no', label: 'No. Opname', nowrap: true },
            { key: 'opname_date', label: 'Tanggal', render: v => fmtDate(v) },
            { key: 'warehouse_nama', label: 'Gudang' },
            { key: 'discrepancy_count', label: 'Selisih', align: 'center', render: v => v > 0 ? <span style={{ color: '#DC2626', fontWeight: 600 }}>{v}</span> : '-' },
            { key: 'status', label: 'Status', render: v => getStatusBadge(v) },
            { key: 'id', label: 'Aksi', render: (id, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="secondary" icon={<Eye size={12}/>} onClick={() => handleView(id)}>Detail</Btn>
                {row.status === 'DRAFT' && (
                  <Btn size="sm" onClick={() => handleReconcile(id)}>Reconcile</Btn>
                )}
              </div>
            )},
          ]} data={opnames} />
        )}
      </Card>

      <Card style={{ marginTop: 16, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: '#1E40AF' }}>Alur Stock Opname</h4>
        <ol style={{ margin: 0, paddingLeft: 20, color: '#1E40AF', fontSize: 13, lineHeight: 1.8 }}>
          <li>Buat session opname baru untuk gudang tertentu</li>
          <li>Lakukan penghitungan fisik barang di gudang</li>
          <li>Bandingkan dengan stok di sistem</li>
          <li>Reconcile untuk menyesuaikan stok</li>
          <li>Sistem otomatis membuat journal penyesuaian</li>
        </ol>
      </Card>

      {showModal && (
        <Modal title="Session Stock Opname Baru" onClose={closeModal}>
          <Field label="Tanggal" required>
            <Input type="date" value={form.opname_date} onChange={e => setForm({ ...form, opname_date: e.target.value })} />
          </Field>
          <Field label="Gudang" required>
            <Select value={form.warehouse_id} onChange={e => setForm({ ...form, warehouse_id: e.target.value })} options={locations} placeholder="Pilih gudang..." />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Buat Session'}</Btn>
          </div>
        </Modal>
      )}

      {viewOpname && (
        <Modal title={`Detail Opname ${viewOpname.opname_no}`} onClose={() => setViewOpname(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Tanggal</strong><p style={{ margin: 2 }}>{fmtDate(viewOpname.opname_date)}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Gudang</strong><p style={{ margin: 2 }}>{viewOpname.warehouse_nama}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Selisih</strong><p style={{ margin: 2 }}>{viewOpname.discrepancy_count || 0}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Status</strong><p style={{ margin: 2 }}>{getStatusBadge(viewOpname.status)}</p></div>
          </div>
          {viewOpname.details?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#F8FAFC' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Barang</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>System Qty</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Fisik Qty</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Variance</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Action</th>
              </tr></thead>
              <tbody>
                {viewOpname.details.map((d, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px 12px' }}>{d.item_nama}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{d.system_quantity}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{d.physical_quantity}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: d.variance > 0 ? '#DC2626' : d.variance < 0 ? '#059669' : '#374151' }}>{d.variance}</td>
                    <td style={{ padding: '8px 12px' }}>{getStatusBadge(d.reconciliation_action)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setViewOpname(null)}>Tutup</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}